import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { downloadLatin1Edifact, downloadLatin1Xml } from '@/lib/referral/latin1Xml';
import { FORM_ERROR_TYPE, FORM_LABEL_TYPE, TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';

export type ReferralCheck = { id: string; pass: boolean; detail: string };

export type ReferralCreatedInfo = {
  outboxId?: string;
  exchangeId?: string;
};

function CheckList({
  checks,
  presentDetail,
  className,
  itemClassName,
}: {
  checks: readonly ReferralCheck[];
  presentDetail?: (detail: string) => string;
  className?: string;
  itemClassName?: string;
}) {
  const { t } = useTranslation();
  return (
    <ul className={className}>
      {checks.map((check, index) => {
        const detail = check.detail
          ? presentDetail
            ? presentDetail(check.detail)
            : check.detail
          : '';
        return (
          <li key={`${check.id}-${index}`} className={itemClassName}>
            {check.pass
              ? t('patients.hospital_referral.check_pass', 'Opfyldt')
              : t('patients.hospital_referral.check_fail', 'Ikke opfyldt')}
            {': '}
            {check.id}
            {detail ? ` — ${detail}` : ''}
          </li>
        );
      })}
    </ul>
  );
}

/** Kontrol rows + XML (and optional EDIFACT) as shown after Dan henvisning. */
export function ReferralBuiltPreview({
  xml,
  checks,
  ok,
  edi,
  xmlFileName,
  ediFileName,
  showOutboxNote = true,
  variant = 'form',
  presentDetail,
}: {
  xml: string;
  checks: readonly ReferralCheck[];
  ok?: boolean | null;
  edi?: string;
  xmlFileName: string;
  ediFileName?: string;
  showOutboxNote?: boolean;
  variant?: 'form' | 'detail';
  presentDetail?: (detail: string) => string;
}) {
  const { t } = useTranslation();
  const hasChecks = checks.length > 0;
  const failedNote =
    hasChecks && ok === false ? (
      <p role="alert" className={cn(variant === 'detail' ? 'mt-s2' : 'mt-2', FORM_ERROR_TYPE)}>
        {t(
          'patients.hospital_referral.not_ok',
          'Kontrollen fandt mangler. Obligatoriske felter mangler i dokumentet.',
        )}
      </p>
    ) : null;
  const checkBody = hasChecks ? (
    <CheckList
      checks={checks}
      presentDetail={presentDetail}
      className={variant === 'detail' ? 'hv-checks' : 'mt-2 space-y-1'}
      itemClassName={variant === 'detail' ? undefined : TYPE.body}
    />
  ) : (
    <p className={variant === 'detail' ? 'hv-checks-empty' : cn('mt-2', TYPE.body, 'text-ds-muted')}>
      {t('patients.hospital_referral.checks_none', 'Ingen gemte kontrollinjer')}
    </p>
  );
  const outboxNote = showOutboxNote ? (
    <p className={variant === 'detail' ? 'hv-sub' : TYPE.body}>
      {t(
        'patients.hospital_referral.result_not_sent',
        'Henvisningen er lagt i udbakken. Den er ikke sendt videre.',
      )}
    </p>
  ) : null;
  const xmlBlock = (
    <>
      <pre
        className={
          variant === 'detail'
            ? 'hv-xml'
            : cn(
                'mt-s4 max-h-96 overflow-auto whitespace-pre-wrap rounded-ctl border border-ds-line bg-ds-sunk p-s3',
                TYPE.small,
              )
        }
      >
        {xml}
      </pre>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        className="mt-3"
        onClick={() => downloadLatin1Xml(xml, xmlFileName)}
      >
        {t('patients.hospital_referral.download', 'Hent XML')}
      </Button>
    </>
  );
  const ediBlock =
    edi !== undefined ? (
      <div className="mt-4">
        <p className={variant === 'detail' ? 'hv-sub' : TYPE.body}>
          {t(
            'patients.specialist_referral.result_edifact',
            'Modtageren læser kun EDIFACT (REF06). Henvisningen er konverteret.',
          )}
        </p>
        <pre
          className={
            variant === 'detail'
              ? 'hv-xml'
              : cn(
                  'mt-s2 max-h-96 overflow-auto whitespace-pre-wrap rounded-ctl border border-ds-line bg-ds-sunk p-s3',
                  TYPE.small,
                )
          }
        >
          {edi}
        </pre>
        {ediFileName ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="mt-3"
            onClick={() => downloadLatin1Edifact(edi, ediFileName)}
          >
            {t('patients.hospital_referral.download_edifact', 'Hent EDIFACT (REF06)')}
          </Button>
        ) : null}
      </div>
    ) : null;

  if (variant === 'detail') {
    return (
      <>
        <div className="hv-box">
          <h3>{t('patients.hospital_referral.checks', 'Kontrol')}</h3>
          <div className="hv-panel">
            {failedNote}
            {checkBody}
          </div>
        </div>
        <div className="hv-box">
          <h3>{t('patients.hospital_referral.result_title', 'Henvisning (XML)')}</h3>
          <div className="hv-panel">
            {outboxNote}
            {xmlBlock}
            {ediBlock}
          </div>
        </div>
      </>
    );
  }

  return (
    <section className="mt-s5">
      <h3 className={TYPE.section}>{t('patients.hospital_referral.result_title', 'Henvisning (XML)')}</h3>
      <div className="mt-s3">
        {outboxNote}
        {failedNote}
        <h4 className={cn('mt-4', FORM_LABEL_TYPE)}>
          {t('patients.hospital_referral.checks', 'Kontrol')}
        </h4>
        {checkBody}
        {xmlBlock}
        {ediBlock}
      </div>
    </section>
  );
}
